import { useEffect, useRef } from "react";
import { houseInfo, type HouseKey } from "@hausplaner/model";
import { Scene } from "../scene/Scene";
import {
  useStore, setMode, setFloor, openSheet, toggleMeasure, undo, redo, nudge, rotateSel, deleteSel, duplicateSel,
  toggleLock, alignToWall, select, setMenu, setEdit, setTool, furnitureAllowed, flushSave, saveStateOf, kindsFor, KIND_LABEL,
  type FloorSel, type SaveState,
} from "../state/store";
import { initApp, openHouse, saveAsCopy, openDoc, restoreBackup, discardBackup } from "../state/layouts";
import { walk } from "../scene/rig";
import { useRoute } from "./router";
import { StartPage } from "./Start";
import { Menu } from "./Menu";
import { CatalogSheet } from "./Catalog";
import { SelectionPanel } from "./SelectionPanel";
import { BuilderSheet } from "./Builder";
import { LinesPanel } from "./LinesPanel";
import { LoginScreen } from "./Login";

export function App() {
  useEffect(() => { void initApp(); }, []);
  useEffect(() => {
    const h = () => { void flushSave(); };
    addEventListener("pagehide", h); return () => removeEventListener("pagehide", h);
  }, []);
  const route = useRoute();
  const me = useStore((s) => s.me);
  const needLogin = useStore((s) => s.needLogin);
  const checked = useStore((s) => s.authChecked);
  if (!checked) return <div className="loading" aria-busy="true">Hausplaner wird geladen …</div>;
  if (needLogin && !me) return <><LoginScreen /><Toast /></>;
  return (
    <>
      {route.page === "start" ? <StartPage /> : <HouseScreen houseKey={route.key} />}
      {needLogin && me && <LoginScreen overlay />}
      <Toast />
    </>
  );
}

function HouseScreen({ houseKey }: { houseKey: HouseKey }) {
  useEffect(() => { void openHouse(houseKey); document.title = `${houseInfo(houseKey).name} · Rosenstraße 30`; }, [houseKey]);
  useKeyboard();
  const ready = useStore((s) => s.houseKey === houseKey);
  if (!ready) return <div className="loading">Haus wird geladen …</div>;
  return (
    <>
      <div id="stage"><Scene /></div>
      <TopBar />
      <FloorRail />
      <Joystick />
      <BottomBar />
      <CatalogSheet />
      <SelectionPanel />
      <LinesPanel />
      <BuilderSheet />
      <Banners />
      <Menu />
    </>
  );
}

const SAVE_TEXT: Record<SaveState, string> = {
  local: "nur in diesem Browser", saving: "speichert …", saved: "gespeichert", error: "Speichern fehlgeschlagen",
  conflict: "Konflikt – bitte prüfen", readonly: "nur ansehen", auth: "abgemeldet",
};

function TopBar() {
  const mode = useStore((s) => s.mode);
  const key = useStore((s) => s.houseKey)!;
  const edit = useStore((s) => s.edit);
  const save = useStore(saveStateOf);
  const err = save === "error" || save === "conflict" || save === "auth";
  // welche Einrichtung und welcher Leitungsplan gerade offen sind
  const docs = useStore((s) => kindsFor(s.houseKey).map((k) => s.docs[k].name).filter(Boolean).join(" · ")) || "–";
  return (
    <div className="top">
      <div className="top-left">
        <button className="burger" id="b-menu" aria-label="Menü öffnen" onClick={() => setMenu(true)}><span /><span /><span /></button>
        <div className="tblock" aria-label="Planinfo">
          <div className="k">Objekt</div><div className="name">Rosenstraße 30</div>
          <div className="k">Zustand</div>
          <div id="state-line"><b>{houseInfo(key).name}</b>{edit && <span className="edit-tag">Bearbeiten</span>}<span className={"meta" + (err ? " err" : "")}> · {SAVE_TEXT[save]}</span></div>
          <div className="k opt">Geöffnet</div><div className="opt meta" id="doc-line">{docs}</div>
        </div>
      </div>
      <div className="seg" role="group" aria-label="Ansicht">
        {(["orbit", "plan", "walk"] as const).map((m) => (
          <button key={m} id={"m-" + m} aria-pressed={mode === m} onClick={() => setMode(m)}>
            {m === "orbit" ? "Außen" : m === "plan" ? "Grundriss" : "Rundgang"}
          </button>
        ))}
      </div>
    </div>
  );
}

function FloorRail() {
  const floor = useStore((s) => s.floor);
  const mode = useStore((s) => s.mode);
  const tool = useStore((s) => s.tool);
  const rows: [FloorSel, string, string][] = [["all", "Alle", "Haus"], [3, "DG", "+8,10"], [2, "OG", "+5,35"], [1, "EG", "+2,60"], [0, "UG", "±0,00"]];
  return (
    <div className="rail" role="group" aria-label="Geschoss">
      {rows.map(([f, b, sm]) => (
        <button key={String(f)} data-floor={String(f)} aria-pressed={floor === f} hidden={f === "all" && (mode !== "orbit" || !!tool)} onClick={() => setFloor(f)}>
          <b>{b}</b><small>{sm}</small>
        </button>
      ))}
    </div>
  );
}

function hintFor(s: ReturnType<typeof useStore.getState>): string {
  if (s.measure) return s.mode === "plan" ? "Messen: zwei Punkte auf dem Boden antippen · für Höhen in Außen oder Rundgang wechseln" : "Messen: zwei Punkte auf Wand, Decke oder Boden antippen";
  if (s.tool === "lines") return s.mode === "plan" ? "Leitungen: Punkte im Grundriss antippen · Enter = fertig · Esc = abbrechen" : "Leitungen: Punkte direkt auf Wand, Decke oder Boden antippen · Enter = fertig";
  if (s.mode === "walk") return "Joystick oder WASD: gehen · ziehen: umschauen · Geschoss rechts: Treppe nehmen";
  if (s.mode === "plan") return s.edit ? "Ziehen: verschieben · Möbel ziehen (dockt an Wände an)" : "Ziehen: verschieben · zwei Finger: zoomen · Leitung oder Möbel antippen für Details";
  return "Ziehen: drehen · zwei Finger: zoomen · Geschoss rechts wählen, um hineinzuschauen";
}

function BottomBar() {
  const s = useStore();
  const furniture = furnitureAllowed(s);
  return (
    <div className="bottom">
      <div className="hint" id="hint">{hintFor(s)}</div>
      <div className="tools">
        {s.edit && furniture && <button className="btn primary" id="b-add" onClick={() => openSheet("catalog")}>+ Möbel</button>}
        {s.edit && <button className="btn" id="b-lines" aria-pressed={s.tool === "lines"} onClick={() => setTool("lines")}>Leitungen</button>}
        <button className="btn" id="b-measure" aria-pressed={s.measure} onClick={toggleMeasure}>Messen</button>
        {s.edit && <button className="btn icon" id="b-undo" aria-label="Rückgängig" title="Rückgängig (Strg+Z)" disabled={!s.hist.canUndo} onClick={undo}>↶</button>}
        {s.edit && <button className="btn icon" id="b-redo" aria-label="Wiederholen" title="Wiederholen (Strg+Y)" disabled={!s.hist.canRedo} onClick={redo}>↷</button>}
        {s.edit && <button className="btn done" id="b-done" onClick={() => setEdit(false)}>Fertig</button>}
      </div>
    </div>
  );
}

function Joystick() {
  const mode = useStore((s) => s.mode);
  const knob = useRef<HTMLElement>(null);
  const pid = useRef<number | null>(null);
  const move = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    let dx = (e.clientX - r.left - r.width / 2) / (r.width / 2), dy = (e.clientY - r.top - r.height / 2) / (r.height / 2);
    const l = Math.hypot(dx, dy); if (l > 1) { dx /= l; dy /= l; }
    walk.jx = dx; walk.jy = dy; if (knob.current) knob.current.style.transform = `translate(${dx * 36}px,${dy * 36}px)`;
  };
  const end = (e: React.PointerEvent) => { if (e.pointerId !== pid.current) return; pid.current = null; walk.jx = walk.jy = 0; if (knob.current) knob.current.style.transform = ""; };
  return (
    <div className="joy" id="joy" hidden={mode !== "walk"}
      onPointerDown={(e) => { pid.current = e.pointerId; e.currentTarget.setPointerCapture(e.pointerId); move(e); }}
      onPointerMove={(e) => { if (e.pointerId === pid.current) move(e); }}
      onPointerUp={end} onPointerCancel={end}>
      <i ref={knob} />
    </div>
  );
}

function Banners() {
  const conflict = useStore((s) => kindsFor(s.houseKey).find((k) => s.docs[k].save === "conflict"));
  const id = useStore((s) => (conflict ? s.docs[conflict].id : null));
  const sheet = useStore((s) => s.sheet);
  const menu = useStore((s) => s.menu);
  const restore = useStore((s) => s.restore);
  if (sheet || menu) return null;
  if (restore) return (
    <div className="banner warn" role="alert">
      <span>In diesem Browser gibt es Änderungen, die nicht gespeichert wurden.</span>
      <button className="btn small primary" onClick={restoreBackup}>Wiederherstellen</button>
      <button className="btn small" onClick={discardBackup}>Verwerfen</button>
    </div>
  );
  if (conflict) return (
    <div className="banner warn" role="alert">
      <span>{KIND_LABEL[conflict].one} wurde auf einem anderen deiner Geräte geändert.</span>
      <button className="btn small" onClick={() => id && void openDoc(conflict, id)}>Neu laden</button>
      <button className="btn small primary" onClick={() => void saveAsCopy(conflict)}>Meine Fassung als Kopie</button>
    </div>
  );
  return null;
}

function Toast() {
  const t = useStore((s) => s.toast);
  return <div className="toast" id="toast" hidden={!t} role="status">{t?.text}</div>;
}

/* ---------- Tastatur ---------- */
function useKeyboard() {
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const tg = (e.target as HTMLElement).tagName; if (tg === "INPUT" || tg === "SELECT" || tg === "TEXTAREA") return;
      const k = e.key.toLowerCase(), mod = e.ctrlKey || e.metaKey;
      const s = useStore.getState();
      if (mod && k === "z") { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if (mod && k === "y") { e.preventDefault(); redo(); return; }
      if (s.selectedId && s.mode !== "walk" && s.edit) {
        const st = e.shiftKey ? 0.1 : 0.01;
        const mv = ({ arrowleft: [-st, 0], arrowright: [st, 0], arrowup: [0, st], arrowdown: [0, -st] } as Record<string, number[]>)[k];
        if (mv) { e.preventDefault(); nudge(mv[0], mv[1]); return; }
        if (k === "r") { rotateSel(e.shiftKey ? Math.PI / 12 : -Math.PI / 2); return; }
        if (k === "delete" || k === "backspace") { e.preventDefault(); deleteSel(); return; }
        if (mod && k === "d") { e.preventDefault(); duplicateSel(); return; }
        if (k === "l") { toggleLock(); return; }
        if (k === "w" && !mod) { alignToWall(); return; }
      }
      if (k === "escape" && s.selectedId) { select(null); return; }
      walk.keys[k] = true;
    };
    const up = (e: KeyboardEvent) => { walk.keys[e.key.toLowerCase()] = false; };
    addEventListener("keydown", down); addEventListener("keyup", up);
    return () => { removeEventListener("keydown", down); removeEventListener("keyup", up); };
  }, []);
}
